import {
	PaymentProvider,
	type PaymentProviderAccount,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Edit2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchProviderAccountsQueryOptions,
	useCreateProviderAccount,
	useDeleteProviderAccount,
	useUpdateProviderAccount,
} from "@/api/billing";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useApiParameters } from "@/hooks/useApiParameters";
import { OrganizationSettingsNavigation } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/OrganizationSettingsNavigation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/payment-methods/",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Pre-load provider accounts
		await context.queryClient.ensureQueryData(
			fetchProviderAccountsQueryOptions(userId, token, orgId),
		);

		return { userId, token, orgId };
	},
	component: PaymentMethodsPage,
});

function PaymentMethodsPage() {
	const { t } = useTranslation();
	const { userId, token, orgId } = Route.useLoaderData();
	// Fetch provider accounts
	const { data: providerAccountsData } = useSuspenseQuery(
		fetchProviderAccountsQueryOptions(userId, token, orgId),
	);
	const providerAccounts = providerAccountsData?.providerAccounts ?? [];

	// Dialog state
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [editingAccount, setEditingAccount] =
		useState<PaymentProviderAccount | null>(null);
	const [deletingAccount, setDeletingAccount] =
		useState<PaymentProviderAccount | null>(null);

	// Form state
	const [provider, setProvider] = useState<PaymentProvider>(
		PaymentProvider.MANUAL,
	);
	const [methodName, setMethodName] = useState("");

	// Mutations
	const createMutation = useCreateProviderAccount();
	const updateMutation = useUpdateProviderAccount();
	const deleteMutation = useDeleteProviderAccount();

	const resetForm = () => {
		setProvider(PaymentProvider.MANUAL);
		setMethodName("");
	};

	const handleOpenAddDialog = () => {
		resetForm();
		setIsAddDialogOpen(true);
	};

	const handleOpenEditDialog = (account: PaymentProviderAccount) => {
		setProvider(account.provider);
		setMethodName(account.accountName);
		setEditingAccount(account);
	};

	const handleCloseDialogs = () => {
		setIsAddDialogOpen(false);
		setEditingAccount(null);
		resetForm();
	};

	const handleCreate = async () => {
		await createMutation.mutateAsync({
			token,
			organizationId: orgId,
			data: {
				provider,
				accountName: methodName,
				supportedMethods: [
					"CASH",
					"PIX",
					"CARD",
					"DEBIT_CARD",
					"BANK_TRANSFER",
				],
			},
		});
		handleCloseDialogs();
	};

	const handleUpdate = async () => {
		if (!editingAccount) return;
		await updateMutation.mutateAsync({
			token,
			organizationId: orgId,
			accountId: editingAccount.id,
			data: { accountName: methodName },
		});
		handleCloseDialogs();
	};

	const handleDelete = async () => {
		if (!deletingAccount) return;
		await deleteMutation.mutateAsync({
			token,
			organizationId: orgId,
			accountId: deletingAccount.id,
		});
		setDeletingAccount(null);
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgSettings.title")}
				description={t("orgSettings.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>
			<div
				className="flex-1 flex h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="payment-methods-settings-container"
			>
				<OrganizationSettingsNavigation activeTab="payment-methods" />
				<div className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between">
							<CardTitle>{t("billing.providerAccounts.title")}</CardTitle>
							<Button onClick={handleOpenAddDialog} size="sm">
								<Plus className="w-4 h-4 mr-2" />
								{t("billing.providerAccounts.addAccount")}
							</Button>
						</CardHeader>
						<CardContent>
							{providerAccounts.length === 0 ? (
								<p className="text-base-content/60 text-center py-8">
									{t("billing.providerAccounts.noAccounts")}
								</p>
							) : (
								<div className="space-y-3">
									{providerAccounts.map((account) => (
										<div
											key={account.id}
											className="flex items-center justify-between p-4 border border-base-300 rounded-lg hover:bg-base-200 transition-colors"
										>
											<div className="flex-1">
												<div className="flex items-center gap-2">
													<span className="font-medium">
														{account.accountName}
													</span>
													{account.isDefault && (
														<span className="badge badge-primary badge-sm">
															{t("billing.providerAccounts.isDefault")}
														</span>
													)}
													{!account.isActive && (
														<span className="badge badge-ghost badge-sm">
															{t("common.inactive")}
														</span>
													)}
												</div>
												<p className="text-sm text-base-content/60">
													{t(
														`billing.providerAccounts.providers.${account.provider}`,
													)}
												</p>
											</div>
											<div className="flex items-center gap-2">
												<Button
													variant="ghost"
													size="sm"
													onClick={() => handleOpenEditDialog(account)}
												>
													<Edit2 className="w-4 h-4" />
												</Button>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => setDeletingAccount(account)}
													className="text-error hover:text-error"
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</div>
										</div>
									))}
								</div>
							)}
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Add/Edit Dialog */}
			<Dialog
				open={isAddDialogOpen || !!editingAccount}
				onOpenChange={handleCloseDialogs}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{editingAccount
								? t("common.edit")
								: t("billing.providerAccounts.addAccount")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						{/* Provider Select - only shown when creating */}
						{!editingAccount && (
							<div className="space-y-2">
								<Label htmlFor="provider">
									{t("billing.providerAccounts.provider")}
								</Label>
								<Select
									value={provider}
									onValueChange={(value) =>
										setProvider(value as PaymentProvider)
									}
								>
									<SelectTrigger id="provider">
										<SelectValue
											placeholder={t("billing.providerAccounts.selectProvider")}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={PaymentProvider.MANUAL}>
											{t("billing.providerAccounts.providers.MANUAL")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						)}
						<div className="space-y-2">
							<Label htmlFor="methodName">
								{t("billing.providerAccounts.methodName")}
							</Label>
							<Input
								id="methodName"
								value={methodName}
								onChange={(e) => setMethodName(e.target.value)}
								placeholder={t(
									"billing.providerAccounts.methodNamePlaceholder",
								)}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="ghost" onClick={handleCloseDialogs}>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={editingAccount ? handleUpdate : handleCreate}
							disabled={
								!methodName.trim() ||
								createMutation.isPending ||
								updateMutation.isPending
							}
						>
							{editingAccount ? t("common.save") : t("common.create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={!!deletingAccount}
				onOpenChange={() => setDeletingAccount(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("common.confirmDelete")}</DialogTitle>
						<DialogDescription>
							{t("common.deleteConfirmation", {
								name: deletingAccount?.accountName,
							})}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeletingAccount(null)}>
							{t("common.cancel")}
						</Button>
						<Button
							variant="destructive"
							onClick={handleDelete}
							disabled={deleteMutation.isPending}
						>
							{t("common.delete")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
